import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useT, useLocaleAwareFormatters } from '@/i18n/locale';
import { StatusBadge } from '@/components/StatusBadge';
import { SeatMeter } from '@/components/SeatMeter';
import { CheckoutButton } from '@/components/CheckoutButton';
import { PinResetDialog } from '@/components/PinResetDialog';
import { ApiError } from '@/api/client';
import {
  useCheckout,
  useInstance,
  useInstanceSeats,
  usePortalSession,
  useReleaseSeat,
  useResetPin,
  useResetSeats,
} from '@/api/hooks';
import { trialDaysLeft } from '@/lib/instances';

export const Route = createFileRoute('/_authenticated/instances/$id')({
  // `?checkout=success|canceled` is appended to the Stripe Checkout success/cancel URLs
  // (BILLING_PLAN.md §9.1). Only "success" drives the polling banner below.
  validateSearch: (search: Record<string, unknown>): { checkout?: 'success' | 'canceled' } => ({
    checkout: search.checkout === 'success' || search.checkout === 'canceled' ? search.checkout : undefined,
  }),
  component: InstanceDetailPage,
});

function InstanceDetailPage() {
  const t = useT();
  const { date } = useLocaleAwareFormatters();
  const { id } = Route.useParams();
  const { checkout: checkoutParam } = Route.useSearch();
  const awaitingCheckout = checkoutParam === 'success';

  const instanceQuery = useInstance(id, {
    // Poll briefly after returning from Stripe Checkout until the webhook has flipped the
    // instance to "active" (BILLING_PLAN.md §13, "?checkout=success ... pollt bis ... active").
    refetchInterval: (query) => {
      if (!awaitingCheckout) return false;
      return query.state.data?.status === 'active' ? false : 3000;
    },
  });
  const seatsQuery = useInstanceSeats(id);
  const checkout = useCheckout(id);
  const portalSession = usePortalSession();
  const releaseSeat = useReleaseSeat(id);
  const resetSeats = useResetSeats(id);
  const resetPin = useResetPin(id);

  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [checkoutSeats, setCheckoutSeats] = useState(1);
  const seatsPrefilled = useRef(false);

  useEffect(() => {
    if (!seatsPrefilled.current && instanceQuery.data) {
      seatsPrefilled.current = true;
      setCheckoutSeats(instanceQuery.data.seatsPurchased || instanceQuery.data.seatsRecommended || 1);
    }
  }, [instanceQuery.data]);

  if (instanceQuery.isPending) {
    return <p className="mt-10 text-center text-sm text-slate-500">{t('generic.loading')}</p>;
  }

  if (instanceQuery.isError) {
    const notFound = instanceQuery.error instanceof ApiError && instanceQuery.error.status === 404;
    return (
      <div className="mt-10 flex flex-col items-center gap-3 text-center">
        <p className="text-sm text-red-600">
          {notFound ? t('instances.detail.notFound') : t('instances.detail.loadError')}
        </p>
        <Link to="/instances" className="text-sm font-medium text-brand-700 hover:text-brand-800">
          ← {t('instances.detail.back')}
        </Link>
      </div>
    );
  }

  const instance = instanceQuery.data;
  const seats = seatsQuery.data?.seats ?? [];
  const activeSeats = seats.filter((s) => !s.released);

  return (
    <div>
      <Link to="/instances" className="text-sm font-medium text-slate-500 hover:text-slate-700">
        ← {t('instances.detail.back')}
      </Link>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-slate-900">{instance.urlLabel ?? instance.identValue}</h1>
        <StatusBadge status={instance.status} trialDaysLeft={trialDaysLeft(instance.trialEndsAt)} />
      </div>

      {awaitingCheckout && instance.status !== 'active' && (
        <div className="mt-4 rounded-lg bg-brand-50 p-3 text-sm text-brand-800 ring-1 ring-inset ring-brand-200">
          <p className="font-medium">{t('instances.detail.checkoutSuccess.title')}</p>
          <p className="mt-1">{t('instances.detail.checkoutSuccess.body')}</p>
        </div>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Section title={t('instances.detail.section.status')}>
          {instance.status === 'trial' && instance.trialEndsAt && (
            <Row>{t('instances.detail.trialEndsAt', { date: date(instance.trialEndsAt) })}</Row>
          )}
          {instance.graceEndsAt && <Row>{t('instances.detail.graceEndsAt', { date: date(instance.graceEndsAt) })}</Row>}
          {instance.currentPeriodEnd && (
            <Row>{t('instances.detail.currentPeriodEnd', { date: date(instance.currentPeriodEnd) })}</Row>
          )}
          {instance.cancelAtPeriodEnd && (
            <Row className="text-amber-700">{t('instances.detail.cancelAtPeriodEnd')}</Row>
          )}
        </Section>

        <Section title={t('instances.detail.section.seats')}>
          <SeatMeter
            purchased={instance.seatsPurchased}
            active={instance.seatsActive}
            recommended={instance.seatsRecommended}
            isTrial={instance.status === 'trial'}
          />
        </Section>

        <Section title={t('instances.detail.section.billing')}>
          <div className="flex flex-col gap-3">
            {instance.hasSubscription ? (
              <CheckoutButton
                label={t('instances.detail.actions.manageSubscription')}
                loading={portalSession.isPending}
                onClick={() => portalSession.mutate()}
              />
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={checkoutSeats}
                  onChange={(e) => setCheckoutSeats(Number(e.target.value))}
                  className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  aria-label={t('instances.detail.checkout.seatsLabel')}
                />
                <CheckoutButton
                  label={t('instances.detail.actions.buySeats')}
                  loading={checkout.isPending}
                  onClick={() => checkout.mutate(checkoutSeats)}
                />
              </div>
            )}
            {(checkout.isError || portalSession.isError) && (
              <p className="text-sm text-red-600">{t('instances.detail.billing.error')}</p>
            )}
          </div>
        </Section>

        <Section title={t('instances.detail.section.pinning')}>
          {instance.pinnedUuid ? (
            <Row className="truncate" title={instance.pinnedUuid}>
              {t('instances.detail.pinning.pinned', { uuid: instance.pinnedUuid })}
            </Row>
          ) : (
            <Row>{t('instances.detail.pinning.unpinned')}</Row>
          )}
          <button
            type="button"
            onClick={() => setPinDialogOpen(true)}
            className="mt-3 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            {t('instances.detail.actions.resetPin')}
          </button>
          {resetPin.isError && <p className="mt-2 text-sm text-red-600">{t('instances.detail.pinning.error')}</p>}
        </Section>
      </div>

      <Section
        title={t('instances.detail.seatList.title')}
        action={
          <button
            type="button"
            disabled={resetSeats.isPending || activeSeats.length === 0}
            onClick={() => {
              if (window.confirm(t('instances.detail.actions.resetAllConfirm'))) {
                resetSeats.mutate();
              }
            }}
            className="text-sm font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50"
          >
            {t('instances.detail.actions.resetAll')}
          </button>
        }
        className="mt-6"
      >
        <p className="mb-3 text-xs text-slate-500">{t('instances.detail.seatList.hint')}</p>
        {seatsQuery.isPending ? (
          <p className="text-sm text-slate-500">{t('generic.loading')}</p>
        ) : seatsQuery.isError ? (
          <p className="text-sm text-red-600">{t('instances.detail.seatList.loadError')}</p>
        ) : activeSeats.length === 0 ? (
          <p className="text-sm text-slate-500">{t('instances.detail.seatList.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-4">{t('instances.detail.seatList.col.hash')}</th>
                  <th className="py-2 pr-4">{t('instances.detail.seatList.col.firstSeen')}</th>
                  <th className="py-2 pr-4">{t('instances.detail.seatList.col.lastSeen')}</th>
                  <th className="py-2 pr-4">{t('instances.detail.seatList.col.appVersion')}</th>
                  <th className="py-2">{t('instances.detail.seatList.col.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {activeSeats.map((seat) => (
                  <tr key={seat.seatHashPrefix} className="border-b border-slate-100">
                    <td className="py-2 pr-4 font-mono text-xs text-slate-600">{seat.seatHashPrefix}</td>
                    <td className="py-2 pr-4 text-slate-600">{date(seat.firstSeen)}</td>
                    <td className="py-2 pr-4 text-slate-600">{date(seat.lastSeen)}</td>
                    <td className="py-2 pr-4 text-slate-600">{seat.appVersion ?? '—'}</td>
                    <td className="py-2">
                      <button
                        type="button"
                        disabled={releaseSeat.isPending}
                        onClick={() => releaseSeat.mutate(seat.seatHashPrefix)}
                        className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                      >
                        {t('instances.detail.actions.releaseSeat')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(releaseSeat.isError || resetSeats.isError) && (
          <p className="mt-3 text-sm text-red-600">{t('generic.error')}</p>
        )}
      </Section>

      <PinResetDialog
        open={pinDialogOpen}
        identType={instance.identType}
        pinnedUuid={instance.pinnedUuid}
        onClose={() => setPinDialogOpen(false)}
        onConfirm={() => {
          resetPin.mutate(undefined, { onSuccess: () => setPinDialogOpen(false) });
        }}
      />
    </div>
  );
}

function Section({
  title,
  action,
  children,
  className = '',
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-5 ${className}`}>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {action}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Row({ children, className = '', title }: { children: ReactNode; className?: string; title?: string }) {
  return (
    <p className={`text-sm text-slate-600 ${className}`} title={title}>
      {children}
    </p>
  );
}
